import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

//context
import { OrgSettingsContext } from "./OrgSettings";

//interfaces
import { PaginationSettings } from "@/interfaces/lists";
import { SortBy } from "@/interfaces/toggle";
import { NotificationStatus } from "@/interfaces/unsplash";
import { AllUsersSortByType, IUserData, IWorkspaceUserGroupData } from "@/interfaces/orgSettings";

//api
import { addAllUsersGroupToWorkspace, getAllUserGroupsForAddNewGroupModal, getAllUserGroupsForWorkspace, removeUserGroupFromWorkspaceAccess, updateUserGroupPermissionForWorkspace } from  "@/api/orgSettings/userGroups";
import { getAllUsersForWorkspace } from  "@/api/orgSettings/users";



export interface IOrgSettingsWorkspaceContext {
  includeArchivedUserGroups: boolean,
  setIncludeArchivedUserGroups: (include: boolean)=> void,
  includeArchivedUsers: boolean,
  setIncludeArchivedUsers: (include: boolean)=> void,
  sortToggle: [AllUsersSortByType, SortBy],
  setSortToggle: ([AllUsersSortByType, SortBy]) => void,
  sortUsersToggle: [AllUsersSortByType, SortBy],
  setSortUsersToggle: ([AllUsersSortByType, SortBy]) => void,
  modalSortToggle: [AllUsersSortByType, SortBy],
  setModalSortToggle: ([AllUsersSortByType, SortBy]) => void,
  setShowAddNewGroupModal: (show: boolean) => void,
  userData: IUserData[];
  showAddNewGroupModal: boolean,
  userGroupData: IWorkspaceUserGroupData[] | undefined,
  isLoading: boolean,
  getUserGroupsCalled: boolean,
  setIsLoading: (load: boolean)=> void,
  newUsersIsLoading: boolean;
  setUserGroupWorkspacePermission: any,
  removeUserGroupFromWorkspace: any,
  searchString: string,
  setSearchString: (search: string) => void,
  searchUsersString: string,
  setSearchUsersString: (search: string) => void,
  searchNewGroupString: string,
  setSearchNewGroupString: (search: string) => void,
  notificationDetails: {show: boolean, status: NotificationStatus, message: string},
  paginationSettings: PaginationSettings,
  userDataPaginationSettings: PaginationSettings,
  getAllUsers: any,
  getAllUserGroups: any,
  addAllUsersGroup: any,
  getNewUserGroups: any,
  newUserGroupData: IWorkspaceUserGroupData[] | undefined,
  moreAvailable: boolean,
}

const orgSettingsWorkspaceDefaults:IOrgSettingsWorkspaceContext = {
  includeArchivedUserGroups: false,
  setIncludeArchivedUserGroups:() => {},
  includeArchivedUsers: false,
  setIncludeArchivedUsers:() => {},
  sortToggle: [AllUsersSortByType.PERMISSIONS, SortBy.DESCENDING],
  setSortToggle: () => {},
  sortUsersToggle: [AllUsersSortByType.USERNAME, SortBy.ASCENDING],
  setSortUsersToggle: () => {},
  modalSortToggle: [AllUsersSortByType.PERMISSIONS, SortBy.DESCENDING],
  setModalSortToggle: () => {},
  setShowAddNewGroupModal: () => {},
  showAddNewGroupModal: false,
  getUserGroupsCalled: false,
  userGroupData: undefined,
  userData: undefined,
  isLoading: false,
  setIsLoading: ()=>{},
  newUsersIsLoading: false,
  setUserGroupWorkspacePermission:() => {},
  removeUserGroupFromWorkspace:() => {},
  searchString: "",
  setSearchString: () => {},
  searchUsersString: "",
  setSearchUsersString: () => {},
  searchNewGroupString: "",
  setSearchNewGroupString: () => {}, 
  notificationDetails: {show: false, status: null, message: ""},
  userDataPaginationSettings: {hasPagination: false, currentPage: 1, totalPages:1, pageSize: 20, totalItems: 0},
  paginationSettings: {hasPagination: false, currentPage: 1, totalPages:1, pageSize: 20, totalItems: 0},
  getAllUserGroups: () => {},
  getAllUsers: () => {},
  addAllUsersGroup: () => {},
  newUserGroupData: undefined,
  getNewUserGroups: () => {},
  moreAvailable: false,
}

export const OrgSettingsWorkspaceContext = createContext(orgSettingsWorkspaceDefaults);

const OrgSettingsWorkspaceProvider = ({children}:{children:React.ReactNode}) => {
  const {appId} = useContext(OrgSettingsContext);
  const top: number = 20;
  const [showAddNewGroupModal, setShowAddNewGroupModal] = useState<boolean>(false);
  const [includeArchivedUserGroups, setIncludeArchivedUserGroups] = useState<boolean>(false);
  const [includeArchivedUsers, setIncludeArchivedUsers] = useState<boolean>(false);
  const [userGroupData, setUserGroupData] = useState<IWorkspaceUserGroupData[]>(undefined);
  const [userData, setUserData] = useState<IUserData[]>(undefined);
  const [newUserGroupData, setNewUserGroupData] = useState<IWorkspaceUserGroupData[] | undefined>([]);
  const [searchNewGroupString, setSearchNewGroupString] = useState<string>("");
  const [searchUsersString, setSearchUsersString] = useState<string>("");
  const [searchString, setSearchString] = useState<string>("");
  const [paginationSettings, setPaginationSettings] = useState<PaginationSettings>({hasPagination: false, currentPage: 1, totalPages:1, pageSize: 20, totalItems: 0});
  const [userDataPaginationSettings, setUserDataPaginationSettings] = useState<PaginationSettings>({hasPagination: false, currentPage: 1, totalPages:1, pageSize: 20, totalItems: 0});
  const [sortToggle, setSortToggle] = useState<[AllUsersSortByType, SortBy]>([AllUsersSortByType.PERMISSIONS, SortBy.DESCENDING]);
  const [sortUsersToggle, setSortUsersToggle] = useState<[AllUsersSortByType, SortBy]>([AllUsersSortByType.USERNAME, SortBy.ASCENDING]);
  const [modalSortToggle, setModalSortToggle] = useState<[AllUsersSortByType, SortBy]>([AllUsersSortByType.GROUPS, SortBy.ASCENDING]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [newUsersIsLoading, setNewUsersIsLoading] = useState<boolean>(false);
  const [notificationDetails, setNotificationDetails] = useState<{show: boolean, status: NotificationStatus, message: string}>({show: false, status: null, message: ""});
  const [skip, setSkip] = useState<number>(0);
  const [moreAvailable, setMoreAvailable] = useState<boolean>(false);
  const [getUserGroupsCalled, setGetUserGroupsCalled]=useState<boolean>(false);
  const [groupAbortController, setGroupAbortController] = useState<AbortController>(null);
  const [userAbortController, setUserAbortController] = useState<AbortController>(null);
  const [modalAbortController, setModalAbortController] = useState<AbortController>(null);


  useEffect(() => {
      if (notificationDetails.show) {
          const timer = setTimeout(() => {
              setNotificationDetails({show: false, status:null, message: ""});
          }, 3000);
          return () => clearTimeout(timer);
      }
  }, [notificationDetails]);

  function getAllUserGroups(page?: number){
      if (!appId) { return }
      setIsLoading(true);

      if (groupAbortController) { groupAbortController.abort(); }
    
      const newAbortController = new AbortController();
      setGroupAbortController(newAbortController);

      let skip = 0;
      if (page) { skip = ((page - 1) * top);} 

      let params = {
          filters: includeArchivedUserGroups ? ['includeInactive'] : [],
          searchTerm: searchString,
          sortBy: sortToggle[0],
          sort: sortToggle[1],
          top: top,
          skip: skip
      };

      getAllUserGroupsForWorkspace(appId, params, { signal: newAbortController.signal })
      .then((data) =>   {
          setUserGroupData(data.records);
          setPaginationSettings({
              hasPagination: data.total > data.top,
              currentPage:  Math.ceil(page ?? 1),
              totalPages: Math.ceil(data.total / data.top),
              pageSize: data.top,
              totalItems: data.total
          })
          setIsLoading(false);
      })
      .catch((error) => {
        if (error.name !== "AbortError" && error.code !== "ERR_CANCELED") {
          setNotificationDetails({ show: true, status: NotificationStatus.error, message: "Unable to get user group data, please refresh the page."});
        }
      })
      .finally(() => {
        setGetUserGroupsCalled(true);
      })
  }

  async function removeUserGroupFromWorkspace(userGroupId: number){
      removeUserGroupFromWorkspaceAccess(appId, userGroupId)
      .then(() => {
        setNotificationDetails({ show: true, status: NotificationStatus.success, message: "Successfully removed user group from workspace."});
      })
      .catch(() => {
          setNotificationDetails({ show: true, status: NotificationStatus.error, message: "Unable to remove user group from this workspace, please try again."});
      })
      .finally(() => {
          getAllUserGroups(paginationSettings.currentPage);
      })
  }

  async function setUserGroupWorkspacePermission(userGroupId: number, setRoleAsEditor: boolean) {
      const params = { permission : setRoleAsEditor ? 'manage' : 'read'}

      updateUserGroupPermissionForWorkspace(appId, userGroupId, params)
      .then(() => {
        setNotificationDetails({ show: true, status: NotificationStatus.success, message: `Successfully Updated Permissions.`});
      })
      .catch(() => {
          setNotificationDetails({ show: true, status: NotificationStatus.error, message: "Unable to set permission for user group, please try again."});
      })
      .finally(() => {
          getAllUserGroups(paginationSettings.currentPage);
      })
  }

  function getNewUserGroups(paginate: 'new' | 'paginate'){
      setNewUsersIsLoading(true)
      if (!appId ) {return}

      if (modalAbortController) { modalAbortController.abort(); }
    
      const newModalAbortController = new AbortController();
      setModalAbortController(newModalAbortController);


      const skipValue: number = paginate === 'paginate' ? skip + top : 0;
      const params = {
          searchTerm: searchNewGroupString,
          sortBy: modalSortToggle[0],
          sort: modalSortToggle[1],
          top: top,
          skip: skipValue,
      }

      getAllUserGroupsForAddNewGroupModal(appId, params, { signal: newModalAbortController.signal })
      .then((data) => {
          setNewUserGroupData(paginate === 'paginate' ? [...newUserGroupData, ...data.records] : data.records);
          setMoreAvailable(data.moreAvailable);
          setSkip(data.skip);
          setNewUsersIsLoading(false);
      })
      .catch((error) => {
        if (error.name !== "AbortError" && error.code !== "ERR_CANCELED") {
          setNotificationDetails({ show: true, status: NotificationStatus.error, message: "Unable to get user group data, please refresh the modal."});
        }
      })
  }

  function getAllUsers(page?: number){
    if (!appId ) {return}
    if (userAbortController) { userAbortController.abort(); }
    
    const newUserAbortController = new AbortController();
    setUserAbortController(newUserAbortController);
    setIsLoading(true);

    let skip = 0;
    if (page) { skip = ((page - 1) * top);} 
    const params = {
        filters: includeArchivedUsers ? ['includeInactive'] : [],
        searchTerm: searchUsersString,
        sortBy: sortUsersToggle[0],
        sort: sortUsersToggle[1],
        top: top,
        skip: skip
    }

    getAllUsersForWorkspace(appId, params, { signal: newUserAbortController.signal })
    .then((data) =>   {
        setUserData(data.records);
        setUserDataPaginationSettings({
            hasPagination: data.total > data.top,
            currentPage:  Math.ceil(page ?? 1),
            totalPages: Math.ceil(data.total / data.top),
            pageSize: data.top,
            totalItems: data.total
        })
    })
    .catch((error) => {
      if (error.name !== "AbortError" && error.code !== "ERR_CANCELED") {
        setNotificationDetails({ show: true, status: NotificationStatus.error, message: "Unable to get user data, please refresh the page."});
      }
    })
  }

  function addAllUsersGroup() {
    addAllUsersGroupToWorkspace(appId)
    .then(() =>   {
      setNotificationDetails({ show: true, status: NotificationStatus.success, message: "Successfully added the 'All Users Group' to workspace."});
    })
    .catch((error) => {
        setNotificationDetails({ show: true, status: NotificationStatus.error, message: "Unable to add 'All Users Group', please try again."});
    })
    .finally(() => {
      getAllUserGroups(paginationSettings.currentPage);
    })
  }

  const context = useMemo(() => (
    {includeArchivedUserGroups, setIncludeArchivedUserGroups, includeArchivedUsers, setIncludeArchivedUsers, setSortToggle, sortToggle, setSortUsersToggle, sortUsersToggle, setShowAddNewGroupModal, showAddNewGroupModal, userGroupData, userData, isLoading, getUserGroupsCalled, setIsLoading, setUserGroupWorkspacePermission, removeUserGroupFromWorkspace, searchString, setSearchString, searchUsersString, setSearchUsersString, notificationDetails, paginationSettings, getAllUserGroups, modalSortToggle, setModalSortToggle, getNewUserGroups, newUserGroupData, moreAvailable, getAllUsers, userDataPaginationSettings, newUsersIsLoading, addAllUsersGroup, searchNewGroupString, setSearchNewGroupString}
  ), [includeArchivedUsers, includeArchivedUserGroups, sortToggle, sortUsersToggle, modalSortToggle, showAddNewGroupModal, userGroupData, isLoading, searchString, searchUsersString, notificationDetails, userDataPaginationSettings, paginationSettings, newUserGroupData, userData, moreAvailable, newUsersIsLoading, getUserGroupsCalled, searchNewGroupString])

    return (
      <OrgSettingsWorkspaceContext.Provider value={context}>
        {children}
      </OrgSettingsWorkspaceContext.Provider>
    );
};

export default OrgSettingsWorkspaceProvider;
